/**
 * Standard naming for AIVIA Finances PDF downloads (AR-199). Single source of
 * truth shared by the NestJS server (Content-Disposition) and the React client
 * (anchor download / print title) so the two can never disagree.
 *
 * Use these helpers for all future finance PDF generations.
 */

/** Formats an ISO date (YYYY-MM-DD) as MMDDYYYY. Returns the input unchanged if it isn't a valid calendar date in that exact format. */
export function formatPdfDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  // Reject impossible calendar dates (e.g. 2026-13-40, 2026-02-30).
  // Note: use setUTCFullYear (not Date.UTC) so years 0000-0099 aren't remapped to 1900-1999.
  const dt = new Date(0);
  dt.setUTCFullYear(Number(year), Number(month) - 1, Number(day));
  if (
    dt.getUTCFullYear() !== Number(year) ||
    dt.getUTCMonth() !== Number(month) - 1 ||
    dt.getUTCDate() !== Number(day)
  ) {
    return isoDate;
  }
  return `${month}${day}${year}`;
}

/** Statement PDF filename, e.g. "AIVIA Finances Statement_05152026.pdf". */
export function statementPdfFilename(statementDate: string): string {
  return `AIVIA Finances Statement_${formatPdfDate(statementDate)}.pdf`;
}

/**
 * Snapshot (Reports) PDF name, e.g. "AIVIA Finances Snapshot_05012026-05312026".
 * No extension — used as the print document title; the browser appends ".pdf".
 */
export function snapshotPdfName(dateFrom: string, dateTo: string): string {
  if (!dateFrom && !dateTo) return "AIVIA Finances Snapshot";
  const from = dateFrom ? formatPdfDate(dateFrom) : "Start";
  const to = dateTo ? formatPdfDate(dateTo) : "Present";
  return `AIVIA Finances Snapshot_${from}-${to}`;
}
